/* eslint-disable camelcase */
import { REPORT_TYPE, buildSSOPackage, convertObjectNumberToInt, getNumNetworkType, isEmpty, isPromise, isRemoteTrack, isUndefined, performanceNow } from './../utils/utils';
import {
  AUDIO_LEVEL_SCALE,
  AUDIO_STAT_BIT,
  AUX_STAT_BIT,
  BASE_HOST,
  IRoom,
  Logger,
  LOGGER_CMD_TYPE,
  MediaType,
  NAME,
  RemoteStreamType,
  SECOND_HOST,
  THIRD_HOST,
  UserRole,
  UserRoleNumber,
  VIDEO_STAT_BIT,
  MULTI_VIDEO_DATA_TYPE,
  version,
  PlayerState,
  RoomEvent,
  Scene,
  IRemoteTrack
} from '..';
import innerEmitter from '../inner-emitter/inner-emitter';
import INNER_EVENT, { InnerEventTypes } from '../inner-emitter/inner-event';
import {
  getLoggerUrl,
  ipv4ToUint32,
  isFunction
} from '../utils/utils';
import RtcError from '../common/rtc-error';
import ErrorCode from '../common/error-code';
import { getDeviceModel, getOSString, getUserAgentData } from '../utils/environment';
import loggerManager from '../utils/log/logger-manager';
import { compressJSON, http } from '../utils/http';
import { addPromiseRetry } from '../utils/decorators/promise-retry';
import timer from '../utils/timer';
import kvStatManager, { KV_REPORT_KEY_VIDEO_PLAYER, KVStatManager, outterKVStatManager } from './kv-stat-manager';
import ssoManager from '../utils/sso/sso-manager';
/**
 * 给函数封装 try catch 逻辑
 * @export
 * @param {Object} { fn, context }
 * @return {Function}
 */
function tryCatchFunction({ fn, context }: { fn: (...args: any) => any; context: any; }) {
  return function (...args: any) {
    try {
      const result = fn.apply(context, args);
      if (isPromise(result)) {
        return result.catch(error => loggerManager.error(`${fn.name}() error observed ${error}`));
      }
      return result;
    } catch (error) {
      loggerManager.error(`${fn.name}() error observed ${error}`);
    }
  };
}

export const enum KeyPointConnectionType {
  SINGLE_PC = 1,
  MULTI_PC = 2,
  WEB_TRANSPORT = 3,
  WEB_SOCKET = 4
}

/**
 * 关键路径上报模块
 * 说明：对齐 native 实现逻辑，生成质量日报，供客户查询质量数据指标。
 * ref: http://tapd.oa.com/TerWebG/prong/stories/view/1020396022864854365
 * @author rychouwei
 * @class KeyPointManager
 */
class KeyPointManager {
  private _frameWorkType: number;
  private _component: number;
  // 框架语言：Vue2 Vue3 React 等
  private _language?: number;
  connectionType: KeyPointConnectionType;
  private _room: IRoom;
  private _signalInfo: SignalInfo = {
    tinyId: undefined,
    clientIp: '',
    signalIp: '',
    relayIp: '',
    relayInnerIp: '',
    relayPort: 0,
    endReportExtend: undefined,
    reportToken: undefined
  };
  private _keyPrefix: string;
  private _log: Logger;
  private _intervalId: number;
  private _firstPublishedUserList: BasePublishedUserInfo[];
  private _networkQuality: {
    totalUplinkRTT: number;
    totalUplinkLoss: number;
    count: number;
    totalDownlinkRTTAndLossMap: Map<string, {
      totalRTT: number;
      totalLoss: number;
      count: number;
      totalAudioDelay?: number;
      audioDelayCount?: number;
      totalVideoDelay?: number;
      videoDelayCount?: number;
    }>;
  };
  private _basicInfo: KeyPointReport.BasicInfo;
  private _pathJoinRoom: KeyPointReport.PathJoinRoom;
  private _pathLeaveRoom: KeyPointReport.PathLeaveRoom;
  // 下行主流视频进房秒开打点。
  // 进房秒开 = 下行主流视频首帧渲染时间 - 进房接口调用时间
  // 1. 最大秒开时间 5s，超过则按 5s 统计
  // 2. 只统计进房前已推流的主播，进房后再推流的主播则不统计
  // 3. stream-added 抛出后未在 100ms 内调用订阅接口的流，不统计
  // 4. 订阅成功后，未在 100ms 内调用 play 接口的流，不统计
  // 5. 只统计主流，不统计辅流
  // 6. 全程未调用 stream.play 的流，不统计秒开
  private _pathMainVideoMap: Map<string, KeyPointReport.PathMainVideoItem>;
  // 订阅秒开 = 下行主流视频首帧渲染时间 - 订阅信令发送时间
  private _subscribedVideoFirstFrameMap = new Map<string, KeyPointReport.PathMainVideoItem>();
  // 下行主流音频秒开打点
  private _pathMainAudioMap: Map<string, KeyPointReport.PathMainAudioItem>;
  // 下行辅流打点，目前只用来记录发送订阅辅流信令的时间点
  private _pathAuxiliaryMap: Map<string, KeyPointReport.PathAuxiliaryItem>;
  // 记录每一路下行的通话数据，包括帧率、分辨率、码率、音视频拉流时长、视频卡顿时长、订阅秒开（发送 订阅cmd 到首帧渲染的耗时）等
  private _remoteStreamStatMap: Map<string, KeyPointReport.RemoteStreamStat>;
  private _localStreamStat: KeyPointReport.LocalStreamStat;
  private _eventMap: Map<INNER_EVENT, (...args: any) => void> = new Map();

  // 统计过去 100次 2s 上报的平均采集健康度。
  private _captureCostSum = 0;
  private _captureCostCount = 0;
  private isDestroyed = false;
  private _lastReportData: any;
  constructor(options: { frameWorkType?: number; room: IRoom; component?: number; connectionType?: KeyPointConnectionType; language?: number; }) {
  // 用于罗盘统计不同端的 pv/uv，默认 30 是 webrtc，其他类型的值由场景化组件（例如：calling）设置
    this._frameWorkType = options.frameWorkType || 30;
    // v4.15.1 之前不上报 component 的时候，后台 component 默认取 0
    this._component = options.component || 0;
    this.connectionType = options.connectionType || KeyPointConnectionType.SINGLE_PC;
    this._language = options.language || 0;
    this._room = options.room;
    this._keyPrefix = 'key_point';

    this._log = loggerManager.createLogger({
      parent: this._room.getLogger(),
      id: 'kpm',
      userId: this._room.userId,
      sdkAppId: this._room.sdkAppId
    });

    // 给 handle 函数封装 try catch 逻辑，避免代码报错影响主流程
    // @ts-ignore
    Object.getOwnPropertyNames(this.__proto__).forEach(key => {
      // @ts-ignore
      if (key.startsWith('handle') && isFunction(this[key])) {
        // @ts-ignore
        this[key] = tryCatchFunction({ fn: this[key], context: this });
      }
    });

    this.initData();
    this.installEvents();
  }

  initData() {
    this._firstPublishedUserList = [];
    this._networkQuality = {
      totalUplinkRTT: 0,
      totalUplinkLoss: 0,
      count: 0,

      // 记录每一路下行的 rtt 和 loss，用于计算平均 rtt & loss
      totalDownlinkRTTAndLossMap: new Map() // Map<userId, { totalRTT, totalLoss, count }>
    };

    this._basicInfo = {
      string_sdk_version: version,
      uint32_os_type: 15, // webrtc
      string_device_name: '', // 由后台解析 ua 相关信息
      string_http_user_agent: navigator.userAgent,
      string_os_version: '',
      uint32_avg_rtt: 0,
      uint32_avg_up_loss: 0,
      uint32_scene: this._room.scene === Scene.LIVE ? 1 : 0, // 场景，0:VideoCall 1:LIVE 2:AudioCall 3:VoiceChatRoom
      uint32_joining_duration: 0, // 本次通话的在房时长
      uint32_networkType: 0, // 网络类型
      uint32_framework: this._frameWorkType,
      uint32_component: this._component,
      uint32_connection_type: this.connectionType,
      uint32_caller_coding_language: this._language,
      string_domain: location.hostname
    };

    // 进房路径打点
    this._pathJoinRoom = {
      uint64_start_time: 0, // 进房开始时间
      // uint64_init_audio_start_time: 0, // 初始化音频开始时间
      // uint64_init_audio_end_time: 0, // 初始化音频结束时间
      // uint64_init_camera_start_time: 0, // 初始化视频开始时间
      // uint64_init_camera_end_time: 0, // 初始化视频结束时间
      uint64_send_request_acc_ip_cmd_start_time: 0, // 连接 WS 开始时间
      uint64_send_request_acc_ip_cmd_end_time: 0, // 连接 WS 结束（包括成功、失败）时间
      uint64_send_request_enter_room_cmd_start_time: 0, // 发送进房信令时间
      uint64_send_request_enter_room_cmd_end_time: 0, // 收到进房信令响应时间
      uint64_send_first_video_frame_time: 0, // 发送视频首帧时间，用于统计采集成功到发送的耗时，不关键
      uint64_recv_userlist_time: 0, // 收到用户列表时间
      uint64_end_time: 0, // 进房完成时间

      // 调度请求总耗时：local + dns + tcp + tls + request + response
      // int32_schedule_cost: 0,
      // int32_schedule_local: 0,
      // int32_schedule_dns: 0,
      // int32_schedule_tcp: 0,
      // int32_schedule_tls: 0,
      // int32_schedule_request: 0,
      // int32_schedule_response: 0,

      // peerConnection 连接相关耗时。
      // int32_dtls_cost: 0,
      // int32_ice_cost: 0,
      // int32_peer_connection_cost: 0

      int32_send_request_acc_ip_cmd_ret: 0, // WS 连接失败错误码
      int32_send_request_enter_room_cmd_ret: 0, // 进房信令错误码
      int32_end_ret: 0, // 0:进房成功；非0进房失败
      int32_whip_pull_https_cost: 0,
      int32_whip_pull_https_ret: 0 // whip 拉流 HTTPS 请求结果，0:成功，非0:失败错误码
    };

    // 退房路径打点
    this._pathLeaveRoom = {
      uint64_start_time: 0, // 退房开始时间
      uint64_send_request_exit_room_cmd_start_time: 0, // 退房信令发送时间
      uint64_send_request_exit_room_cmd_end_time: 0, // 收到退房信令响应时间，Web 端无退房信令 RES，可认为 RES 时间与发信令时间相同
      uint64_end_time: 0, // 退房结束时间
      int32_send_request_exit_room_cmd_ret: 0, // 退房请求错误码
      int32_end_ret: 0 // 0:退房成功；1：退房请求失败
    };

    // 记录上行的通话数据，包括帧率、分辨率、码率等
    this._localStreamStat = {
      totalVideoBitrate: 0,
      totalVideoFPS: 0,
      totalVideoHeight: 0,
      totalVideoWidth: 0,
      totalAudioLevel: 0,
      videoCount: 0,
      audioLevelCount: 0,
      publishStartTime: 0, // 记录推流接口调用时间
      statsToReport: {
        uint32_audio_capture_db: 0, // 通话过程平均采集 db， web 用 audioLevel 替代
        uint32_video_big_capture_fps: 0, // 通话过程平均帧率
        uint32_video_big_bitrate: 0, // 通话过程平均视频码率
        uint32_video_big_resolution: 0, // 通话过程平均分辨率 videoWidth<<16 | videoHeight
        // uint32_audio_silent_strict: 0 // 通话严格无声率
        uint32_audio_capture_thread_health_zero_cnt: 0, // 采集健康度为 0 的次数
        uint32_after3a_silence_duration: 0 // 3A 处理后无声时长
      }
    };

    this._pathMainVideoMap = new Map();
    this._pathMainAudioMap = new Map();
    this._pathAuxiliaryMap = new Map();
    this._remoteStreamStatMap = new Map();
    this._subscribedVideoFirstFrameMap = new Map();

    getUserAgentData().then(() => {
      this._basicInfo.string_os_version = getOSString();
      this._basicInfo.string_device_name = getDeviceModel() || this._basicInfo.string_os_version;
    });
  }

  addEvent<T extends INNER_EVENT>(name: T, fn: (...args: InnerEventTypes[T]) => void) {
    this._eventMap.set(name, fn);
    innerEmitter.on(name, fn);
    return this;
  }

  installEvents() {
    this.handleUnload = this.handleUnload.bind(this);
    // https://developer.chrome.com/docs/web-platform/deprecating-unload?hl=zh-cn#options_comparison
    // known issue: 1. firefox 关页面不触发 2. edge 刷新页面不触发
    window.addEventListener('pagehide', this.handleUnload);

    this._room.once(RoomEvent.BANNED, () => this.handleLeaveSuccess({ room: this._room, roomId: this._room.roomId }));
    this.addEvent(INNER_EVENT.JOIN_START, this.handleJoinStart)
      .addEvent(INNER_EVENT.JOIN_SCHEDULE_SUCCESS, this.handleJoinScheduleSuccess)
      .addEvent(INNER_EVENT.JOIN_SIGNAL_CONNECTION_START, this.handleSignalConnectionStart)
      .addEvent(INNER_EVENT.JOIN_SIGNAL_CONNECTION_END, this.handleSignalConnectionEnd)
      .addEvent(INNER_EVENT.JOIN_SEND_CMD, this.handleJoinSendCMD)
      .addEvent(INNER_EVENT.JOIN_RECEIVED_CMD_RES, this.handleJoinReceivedCMDResponce)
      .addEvent(INNER_EVENT.WHIP_REQUEST_COST, this.handleWhipRequestCost)
      .addEvent(INNER_EVENT.WHIP_PULL_RESULT, this.handleWhipPullResult)
      .addEvent(INNER_EVENT.JOIN_SUCCESS, this.handleJoinSuccess)
      .addEvent(INNER_EVENT.JOIN_FAILED, this.handleJoinFailed)
      // 退房
      .addEvent(INNER_EVENT.LEAVE_START, this.handleLeaveStart)
      .addEvent(INNER_EVENT.LEAVE_SUCCESS, this.handleLeaveSuccess)
      .addEvent(INNER_EVENT.LEAVE_SEND_CMD, this.handleLeaveSendCMD)
      // 采集
      .addEvent(INNER_EVENT.LOCAL_TRACK_CAPTURE_START, this.handleTrackCaptureStart)
      .addEvent(INNER_EVENT.LOCAL_TRACK_CAPTURE_SUCCESS, this.handleTrackCaptureSuccess)
      .addEvent(INNER_EVENT.LOCAL_TRACK_CAPTURE_FAILED, this.handleTrackCaptureFailed)
      // 推流
      .addEvent(INNER_EVENT.PUBLISH_START, this.handlePublishStart)
      .addEvent(INNER_EVENT.SEND_FIRST_VIDEO_FRAME, this.handleSendFirstVideoFrame)
      // 订阅
      .addEvent(INNER_EVENT.SUBSCRIBE_START, this.handleSubscribeStart)
      .addEvent(INNER_EVENT.SUBSCRIBE_SUCCESS, this.handleSubscribed)
      // 播放
      .addEvent(INNER_EVENT.PLAY_TRACK_START, this.handlePlayStart)
      .addEvent(INNER_EVENT.VIDEO_LOADED_DATA, this.handleVideoLoadedData)
      .addEvent(INNER_EVENT.PLAYER_STATE_CHANGED, (({ track, state, type }) => {
        if (!track.isRemote || !this.hitTest(track.room)) return;

        if (state === PlayerState.PLAYING) {
          if (type === NAME.AUDIO) {
            this.handleAudioPlaying(track as IRemoteTrack);
          } else {
            this.handleVideoPlaying(track as IRemoteTrack);
          }
        }
      }))
      // 切换房间
      .addEvent(INNER_EVENT.SWITCH_ROOM_START, this.handleSwitchRoomStart)
      .addEvent(INNER_EVENT.SWITCH_ROOM_SUCCESS, this.handleSwitchRoomSuccess)
      .addEvent(INNER_EVENT.SWITCH_ROOM_FAILED, this.handleSwitchRoomFailed)
      // 其他
      .addEvent(INNER_EVENT.NETWORK_QUALITY, this.handleNetworkQuality)
      .addEvent(INNER_EVENT.HEARTBEAT_REPORT, this.handleHeartbeatStats)
      .addEvent(INNER_EVENT.RECEIVED_PUBLISHED_USER_LIST, this.handleReceivedPublishUserList)
      .addEvent(INNER_EVENT.REMOTE_PUBLISH_STATE_CHANGED, ({ room, prevMuteState, muteState }) => {
        if (!this.hitTest(room)) return;

        const prevHasMainStream = prevMuteState.hasAudio || prevMuteState.hasVideo || prevMuteState.hasSmall;
        const prevHasAuxStream = prevMuteState.hasAuxiliary;
        const hasMainStream = muteState.hasAudio || muteState.hasVideo || muteState.hasSmall;
        const hasAuxStream = muteState.hasAuxiliary;

        if (!prevHasMainStream && hasMainStream) {
          this.handleRemoteStreamAdded(muteState.userId, RemoteStreamType.Main);
        }
        if (!prevHasAuxStream && hasAuxStream) {
          this.handleRemoteStreamAdded(muteState.userId, RemoteStreamType.Aux);
        }
      })
      .addEvent(INNER_EVENT.SINGLE_CONNECTION_STAT, ({ room, stat }) => {
        if (this.hitTest(room)) {
          this._pathJoinRoom.int32_ice_cost = stat.ice;
          this._pathJoinRoom.int32_dtls_cost = stat.dtls;
          this._pathJoinRoom.int32_peer_connection_cost = stat.peerConnection;
        }
      });
  }

  uninstallEvents() {
    window.removeEventListener('pagehide', this.handleUnload);
    this._eventMap.forEach((listener, eventName) => innerEmitter.off(eventName, listener));
    this._eventMap.clear();
  }

  destroy() {
    this.uninstallEvents();
    timer.clearTask(this._intervalId);
    if (this._pathJoinRoom.uint64_start_time === 0) {
      // @ts-ignore
      this._room = null;
    }
    this.isDestroyed = true;
  }

  handleUnload() {
    if (this._room.isJoined) {
      this.handleLeaveSuccess({ room: this._room, roomId: this._room.roomId });
    }
  }

  handleJoinStart(options: InnerEventTypes[INNER_EVENT.JOIN_START][0]) {
    if (this.hitTest(options.room)) {
      if (this._pathJoinRoom.uint64_start_time === 0) {
        this._pathJoinRoom.uint64_start_time = Date.now();
      }
      if (options.params) {
        if (!isUndefined(options.params.frameWorkType)) {
          this._frameWorkType = options.params.frameWorkType;
          this._basicInfo.uint32_framework = this._frameWorkType;
        }
        if (!isUndefined(options.params.component)) {
          this._component = options.params.component;
          this._basicInfo.uint32_component = this._component;
        }
        if (!isUndefined(options.params.language)) {
          this._language = options.params.language;
          this._basicInfo.uint32_caller_coding_language = this._language;
        }
      }
    }
  }

  handleJoinScheduleSuccess({ room, detailCost }: InnerEventTypes[INNER_EVENT.JOIN_SCHEDULE_SUCCESS][0]) {
    if (this.hitTest(room) && detailCost) {
      const { totalCost, local: beforeDNS, dns, tcp, tls, request, response } = detailCost;
      this._pathJoinRoom.int32_schedule_cost = totalCost;
      this._pathJoinRoom.int32_schedule_local = beforeDNS;
      this._pathJoinRoom.int32_schedule_dns = dns;
      this._pathJoinRoom.int32_schedule_tcp = tcp;
      this._pathJoinRoom.int32_schedule_tls = tls;
      this._pathJoinRoom.int32_schedule_request = request;
      this._pathJoinRoom.int32_schedule_response = response;
    }
  }

  handleSignalConnectionStart({ room }: InnerEventTypes[INNER_EVENT.JOIN_SIGNAL_CONNECTION_START][0]) {
    if (
      this.hitTest(room) &&
      this._pathJoinRoom.uint64_send_request_acc_ip_cmd_start_time === 0
    ) {
      this._pathJoinRoom.uint64_send_request_acc_ip_cmd_start_time = Date.now();
    }
  }

  handleSignalConnectionEnd({ room, error }: InnerEventTypes[INNER_EVENT.JOIN_SIGNAL_CONNECTION_END][0]) {
    if (this.hitTest(room) && this._pathJoinRoom.uint64_send_request_acc_ip_cmd_end_time === 0) {
      this._pathJoinRoom.uint64_send_request_acc_ip_cmd_end_time = Date.now();
      if (error) {
        this._pathJoinRoom.int32_send_request_acc_ip_cmd_ret =
          error instanceof RtcError
            ? Number(error.getExtraCode() || error.getCode())
            : ErrorCode.UNKNOWN;
        this._pathJoinRoom.int32_end_ret = this._pathJoinRoom.int32_send_request_acc_ip_cmd_ret;
      }
    }
  }

  handleJoinSendCMD(options: InnerEventTypes[INNER_EVENT.JOIN_SEND_CMD][0]) {
    if (
      this.hitTest(options.room) &&
      this._pathJoinRoom.uint64_send_request_enter_room_cmd_start_time === 0
    ) {
      this._pathJoinRoom.uint64_send_request_enter_room_cmd_start_time = Date.now();
    }
  }

  handleJoinReceivedCMDResponce(options: InnerEventTypes[INNER_EVENT.JOIN_RECEIVED_CMD_RES][0]) {
    if (
      this.hitTest(options.room) &&
      this._pathJoinRoom.uint64_send_request_enter_room_cmd_end_time === 0
    ) {
      this._pathJoinRoom.uint64_send_request_enter_room_cmd_end_time = Date.now();
      this._pathJoinRoom.int32_send_request_enter_room_cmd_ret = options.code;
      if (options.code !== 0) {
        this._pathJoinRoom.int32_end_ret = this._pathJoinRoom.int32_send_request_enter_room_cmd_ret;
      }
    }
  }

  handleWhipRequestCost(options: InnerEventTypes[INNER_EVENT.WHIP_REQUEST_COST][0]) {
    if (
      this.hitTest(options.room) &&
      this._pathJoinRoom.int32_whip_pull_https_cost === 0
    ) {
      this._pathJoinRoom.int32_whip_pull_https_cost = options.cost;
    }
  }

  handleWhipPullResult(options: InnerEventTypes[INNER_EVENT.WHIP_PULL_RESULT][0]) {
    if (this.hitTest(options.room)) {
      this._pathJoinRoom.int32_whip_pull_https_ret = options.code;
    }
  }

  handleJoinSuccess(options: InnerEventTypes[INNER_EVENT.JOIN_SUCCESS][0]) {
    if (this.hitTest(options.room) && this._pathJoinRoom.uint64_end_time === 0) {
      this._pathJoinRoom.uint64_end_time = Date.now();
      this._pathJoinRoom.int32_end_ret = 0;
      this._signalInfo = options.room.getSignalInfo();
    }
  }

  handleJoinFailed({ room, error }: InnerEventTypes[INNER_EVENT.JOIN_FAILED][0]) {
    if (this.hitTest(room)) {
      this._pathJoinRoom.uint64_end_time = Date.now();
      // 进房失败，int32_end_ret 错误码填进房信令失败错误码 或 ws 连接失败错误码
      if (this._pathJoinRoom.int32_end_ret === 0) {
        this._pathJoinRoom.int32_end_ret = error.code || this._pathJoinRoom.int32_send_request_enter_room_cmd_ret || this._pathJoinRoom.int32_send_request_acc_ip_cmd_ret;
      }
      // 进房失败后上报数据
      // 为避免遗漏 trtc.enterRoom failed 的成功率上报，此处加个 timeout
      setTimeout(() => {
        this.report();
      });
    }
  }

  handleReceivedPublishUserList(options: InnerEventTypes[INNER_EVENT.RECEIVED_PUBLISHED_USER_LIST][0]) {
    // 只在收到第一个 cmd 23（已推流的用户列表） 时，打点。
    if (this.hitTest(options.room) && this._pathJoinRoom.uint64_recv_userlist_time === 0) {
      // whip 拉流播放成功时, 可能还未收到 cmd 23，导致秒开无法统计. 需先把 quickStart 指定的远端用户预填充到 _firstPublishedUserList
      if (!options.isWhipPreFill) this._pathJoinRoom.uint64_recv_userlist_time = Date.now();
      this._firstPublishedUserList = (options.publishedUserList) || [];
    }
  }

  handleSendFirstVideoFrame({ room }: InnerEventTypes[INNER_EVENT.SEND_FIRST_VIDEO_FRAME][0]) {
    if (!this.hitTest(room)) return;
    if (
      this._pathJoinRoom.uint64_send_first_video_frame_time === 0 && this._pathJoinRoom.uint64_start_time !== 0
    ) {
      this._pathJoinRoom.uint64_send_first_video_frame_time = Date.now();
    }
  }

  handleLeaveStart(options: InnerEventTypes[INNER_EVENT.LEAVE_START][0]) {
    if (this.hitTest(options.room)) {
      this._pathLeaveRoom.uint64_start_time = Date.now();
    }
  }

  handleLeaveSuccess(options: InnerEventTypes[INNER_EVENT.LEAVE_SUCCESS][0]) {
    if (this.hitTest(options.room) && this._pathLeaveRoom.uint64_end_time === 0) {
      this._pathLeaveRoom.uint64_end_time = Date.now();
      // 进房完成时间 = 0，说明是异常数据，不处理
      if (this._pathJoinRoom.uint64_end_time !== 0) {
        this._basicInfo.uint32_joining_duration =
          this._pathLeaveRoom.uint64_end_time - this._pathJoinRoom.uint64_end_time;
        const after3aSilenceStartTime = this._room.audioManager.localAudioTrack?.after3aSilenceStartTime;
        if (after3aSilenceStartTime) this._localStreamStat.statsToReport.uint32_after3a_silence_duration = performanceNow() - after3aSilenceStartTime;
      } else {
        this._log.warn('pathJoinRoom endTime is 0');
      }
      // 退房成功后上报数据
      this.report();
    }
  }

  handleLeaveSendCMD(options: InnerEventTypes[INNER_EVENT.LEAVE_SEND_CMD][0]) {
    if (this.hitTest(options.room)) {
      // Web 端退房信令无 RES，默认认为 RES 时间与发信令时间相同
      this._pathLeaveRoom.uint64_send_request_exit_room_cmd_start_time = Date.now();
      this._pathLeaveRoom.uint64_send_request_exit_room_cmd_end_time = Date.now();
    }
  }

  handleSwitchRoomStart(options: InnerEventTypes[INNER_EVENT.SWITCH_ROOM_START][0]) {
    if (this.hitTest(options.room)) {
      const currentTime = Date.now();
      if (this._pathJoinRoom.uint64_end_time !== 0) {
        this._basicInfo.uint32_joining_duration = currentTime - this._pathJoinRoom.uint64_end_time;
      }
      this.report().then(() => {
        this._pathJoinRoom.uint64_start_time = currentTime;
        this._pathJoinRoom.uint64_send_request_enter_room_cmd_start_time = currentTime;
      });
    }
  }

  handleSwitchRoomSuccess({ room }: InnerEventTypes[INNER_EVENT.SWITCH_ROOM_SUCCESS][0]) {
    if (this.hitTest(room) && this._pathJoinRoom.uint64_end_time === 0) {
      const currentTime = Date.now();
      this._pathJoinRoom.uint64_send_request_enter_room_cmd_end_time = currentTime;
      this._pathJoinRoom.uint64_end_time = currentTime;
      this._pathJoinRoom.int32_end_ret === 0;
    }
  }

  handleSwitchRoomFailed({ room, error }: InnerEventTypes[INNER_EVENT.SWITCH_ROOM_FAILED][0]) {
    if (this.hitTest(room)) {
      const currentTime = Date.now();
      this._pathJoinRoom.uint64_send_request_enter_room_cmd_end_time = currentTime;
      this._pathJoinRoom.uint64_end_time = currentTime;
      if (error) {
        this._pathJoinRoom.int32_end_ret = error instanceof RtcError
          ? Number(error.getExtraCode() || error.getCode())
          : ErrorCode.UNKNOWN;
      }
    }
  }

  handleRemoteStreamAdded(userId: string, streamType: RemoteStreamType) {
    const key = `${userId}_${streamType}`;

    if (!this._remoteStreamStatMap.has(key)) {
      const data = {
        userId,
        totalVideoFPS: 0,
        totalVideoBitrate: 0,
        totalAudioLevel: 0,
        totalAudioBitrate: 0,
        totalLoss: 0,
        audioCount: 0,
        audioLevelCount: 0,
        videoCount: 0,
        networkQualityCount: 0,
        streamAddedTime: Date.now(), // stream-added 时间点
        subscribeStartTime: 0, // 调用 subscribe 接口时间点
        subscribedTime: 0, // stream-subscribed 时间点
        playStreamTime: 0, // 调用 stream.play 时间点
        statsToReport: {
          ...REMOTE_STREAM_STATS,
          msg_user_info: new UserInfo({
            userId,
            tinyId: this._room.remotePublishedUserMap.get(userId)?.tinyId,
            role: UserRoleNumber.ANCHOR
          })
        }
      };
      // 参考 native 上报，2 主流，7 辅流
      data.statsToReport.uint32_stream_type = streamType === RemoteStreamType.Main ? 2 : 7;

      this._remoteStreamStatMap.set(key, data);
    }
  }

  handleSubscribeStart({ room, remotePublishedUser, streamType, subscribeState }: InnerEventTypes[INNER_EVENT.SUBSCRIBE_START][0]) {
    if (!this.hitTest(room)) return;
    const { userId, tinyId, role } = remotePublishedUser;
    const userInfo = new UserInfo({ userId, tinyId, role: role === UserRole.ANCHOR ? UserRoleNumber.ANCHOR : UserRoleNumber.AUDIENCE });
    const now = Date.now();
    const key = `${userId}_${streamType}`;
    const data = this._remoteStreamStatMap.get(key);
    if (data && data.subscribeStartTime === 0) {
      data.subscribeStartTime = now;
    }

    // 主流
    if (streamType === RemoteStreamType.Main) {
      // 发送订阅信令时，若远端有推摄像头，且订阅摄像头，则打点统计视频秒开
      if (
        remotePublishedUser.muteState.hasVideo &&
        (subscribeState.video || subscribeState.smallVideo) &&
        !this._pathMainVideoMap.has(key)
      ) {
        this._pathMainVideoMap.set(key, {
          statsToReport: {
            msg_user_info: userInfo,
            uint64_start_enter_time: this._pathJoinRoom.uint64_start_time,
            uint64_render_first_frame_time: 0,
            uint64_combine_first_frame_time: 0
            // uint64_pc_connected_time: 0
          },
          userId,
          sendSubscribeCMDTime: now
        });
        if (!this.hasVideoFlag(userId)) {
          this._subscribedVideoFirstFrameMap.set(key, {
            statsToReport: {
              msg_user_info: userInfo,
              uint64_request_time: now,
              uint64_render_first_frame_time: 0,
              uint64_combine_first_frame_time: 0
            },
            userId,
            sendSubscribeCMDTime: now
          });
        }
      }
      // 发送订阅信令时，若远端有推麦克风，且订阅麦克风，则打点统计音频秒开
      if (
        remotePublishedUser.muteState.hasAudio &&
        subscribeState.audio &&
        !this._pathMainAudioMap.has(key)
      ) {
        this._pathMainAudioMap.set(key, {
          statsToReport: {
            msg_user_info: userInfo,
            uint64_start_enter_time: this._pathJoinRoom.uint64_start_time,
            uint64_play_first_frame_time: 0
          },
          userId,
          sendSubscribeCMDTime: now
        });
      }
    } else {
      // 辅流
      if (
        remotePublishedUser.muteState.hasAuxiliary &&
        subscribeState.auxiliary &&
        !this._pathAuxiliaryMap.has(key)
      ) {
        this._pathAuxiliaryMap.set(key, {
          sendSubscribeCMDTime: now
        });
      }
    }
  }

  handleSubscribed({ room, remotePublishedUser, streamType }: InnerEventTypes[INNER_EVENT.SUBSCRIBE_SUCCESS][0]) {
    if (this.hitTest(room)) {
      const key = `${remotePublishedUser.userId}_${streamType}`;
      const data = this._remoteStreamStatMap.get(key);
      if (data && data.subscribedTime === 0) {
        data.subscribedTime = Date.now();
      }
    }
  }

  /**
   * 用户调用播放接口
   * 用途：记录调用 play 接口的时间，若流订阅成功到用户调用 play 接口的耗时超过 100ms，
   * 则说明用户在订阅成功后，没有立即调用 play 接口，此时无需统计订阅首帧渲染耗时。
   * @param {Object} { stream }
   * @memberof KeyPointManager
   */
  handlePlayStart({ track }: InnerEventTypes[INNER_EVENT.PLAY_TRACK_START][0]) {
    if (!track.isRemote || !this.hitTest(track.room)) return;


    // const downlinkConnection = stream.getConnection();
    // const userId = downlinkConnection.getUserId();
    const key = `${track.userId}_${track.streamType}`;
    const data = this._remoteStreamStatMap.get(key);
    if (data?.playStreamTime === 0) {
      data.playStreamTime = Date.now();
    }
  }

  handleVideoLoadedData({ track }: InnerEventTypes[INNER_EVENT.VIDEO_LOADED_DATA][0]) {
    if (!track.isRemote || !this.hitTest(track.room)) return;

    const key = `${track.userId}_${track.streamType}`;
    const data = this._pathMainVideoMap.get(key);
    if (data && data.statsToReport.uint64_combine_first_frame_time === 0) {
      data.statsToReport.uint64_combine_first_frame_time = Date.now();
    }

    const subscribedUser = this._subscribedVideoFirstFrameMap.get(key);
    if (subscribedUser && subscribedUser.statsToReport.uint64_combine_first_frame_time === 0) {
      subscribedUser.statsToReport.uint64_combine_first_frame_time = Date.now();
    }
  }

  /**
   * 统计视频首帧耗时
   * @memberof KeyPointManager
   */
  handleVideoPlaying(track: IRemoteTrack) {
    const key = `${track.userId}_${track.streamType}`;
    const now = Date.now();
    const pathMainVideoItem = this._pathMainVideoMap.get(key);
    const remoteStreamStat = this._remoteStreamStatMap.get(key);

    const subscribedVideoFirstFrameItem = this._subscribedVideoFirstFrameMap.get(key);

    // 进房秒开：首帧渲染 - 进房开始的耗时
    if (remoteStreamStat) {
      const { statsToReport } = remoteStreamStat;
      if (!statsToReport.uint32_video_render_first && (track.streamType === RemoteStreamType.Main ? this.hasVideoFlag(track.userId) : this.hasAuxFlag(track.userId))) {
        const renderFirst = now - this._pathJoinRoom.uint64_start_time;
        statsToReport.uint32_video_render_first = renderFirst;
        kvStatManager.addNumber({ key: KV_REPORT_KEY_VIDEO_PLAYER.RENDER_FIRST_FRAME_TIME, value: renderFirst });
      }
    }

    if (pathMainVideoItem?.statsToReport.uint64_render_first_frame_time === 0) {
      pathMainVideoItem.statsToReport.uint64_render_first_frame_time = now;
    }
    if (subscribedVideoFirstFrameItem?.statsToReport.uint64_render_first_frame_time === 0) {
      subscribedVideoFirstFrameItem.statsToReport.uint64_render_first_frame_time = now;
    }
  }

  /**
   * 统计音频首帧耗时
   * @memberof KeyPointManager
   */
  handleAudioPlaying(track: IRemoteTrack) {
    const key = `${track.userId}_${track.streamType}`;
    const data = this._pathMainAudioMap.get(key);
    if (data && data.statsToReport.uint64_play_first_frame_time === 0) {
      data.statsToReport.uint64_play_first_frame_time = Date.now();
    }
  }

  handleNetworkQuality(options: InnerEventTypes[INNER_EVENT.NETWORK_QUALITY][0]) {
    if (this.hitTest(options.room)) {
      this._networkQuality.totalUplinkLoss += options.uplink.loss;
      this._networkQuality.totalUplinkRTT += options.uplink.rtt;
      this._networkQuality.count++;

      options.downlinks.forEach(({ rtt, loss, userId, videoDelay, audioDelay }) => {
        const data = this._networkQuality.totalDownlinkRTTAndLossMap.get(userId);
        if (data) {
          data.totalRTT += rtt;
          data.totalLoss += loss;
          if (videoDelay) {
            data.totalVideoDelay = (data.totalVideoDelay || 0) + videoDelay;
            data.videoDelayCount = (data.videoDelayCount || 0) + 1;
          }
          if (audioDelay) {
            data.totalAudioDelay = (data.totalAudioDelay || 0) + audioDelay;
            data.audioDelayCount = (data.audioDelayCount || 0) + 1;
          }
          data.count++;
        } else {
          let totalAudioDelay; let totalVideoDelay; let videoDelayCount; let audioDelayCount;
          if (videoDelay) {
            totalVideoDelay = videoDelay;
            videoDelayCount = 1;
          }
          if (audioDelay) {
            totalAudioDelay = audioDelay;
            audioDelayCount = 1;
          }
          this._networkQuality.totalDownlinkRTTAndLossMap.set(userId, {
            totalRTT: rtt,
            totalLoss: loss,
            count: 1,
            totalAudioDelay,
            totalVideoDelay,
            audioDelayCount,
            videoDelayCount
          });
        }
      });
    }
  }

  /**
   * 监听 2s 心跳事件（SDK 会在 2s 心跳中向仪表盘上报通话数据）
   * 该模块从心跳事件中获取上、下行通话数据，例如：fps，码率，分辨率，音量等。
   * @param {Object} options
   * @memberof KeyPointManager
   */
  handleHeartbeatStats(options: InnerEventTypes[INNER_EVENT.HEARTBEAT_REPORT][0]) {
    if (this.hitTest(options.room)) {
      const { msg_device_info, msg_up_stream_info, msg_down_stream_info } = options.report;
      // 本地视频推流统计数据
      if (msg_up_stream_info.msg_video_status[0]) {
        const {
          uint32_video_codec_bitrate,
          uint32_video_enc_fps,
          uint32_video_width,
          uint32_video_height
        } = msg_up_stream_info.msg_video_status[0];
        this._localStreamStat.totalVideoBitrate += uint32_video_codec_bitrate!;
        this._localStreamStat.totalVideoFPS += uint32_video_enc_fps!;
        this._localStreamStat.totalVideoWidth += uint32_video_width!;
        this._localStreamStat.totalVideoHeight += uint32_video_height!;
        this._localStreamStat.videoCount++;
      }
      // 本地音频统计：忽略 audioLevel 为 0 的数据
      if (msg_up_stream_info.msg_audio_status) {
        const { uint32_audio_level } = msg_up_stream_info.msg_audio_status;
        if (Math.floor((uint32_audio_level! / AUDIO_LEVEL_SCALE) * 100) > 0) {
          this._localStreamStat.totalAudioLevel += uint32_audio_level! / AUDIO_LEVEL_SCALE;
          this._localStreamStat.audioLevelCount++;
        }
      }

      msg_down_stream_info.forEach(stat => {
        const { msg_user_info, msg_audio_status, msg_video_status } = stat;
        const userId = msg_user_info.str_identifier;
        const remotePublishedUser = this._room.remotePublishedUserMap.get(userId);
        msg_video_status.forEach(videoInfoItem => {
          const isMainStream = videoInfoItem.uint32_video_stream_type === MULTI_VIDEO_DATA_TYPE.MULTI_DATA_BIG_IMG;
          const isAuxStream = videoInfoItem.uint32_video_stream_type === MULTI_VIDEO_DATA_TYPE.MULTI_DATA_AUX_IMG;

          const key = `${userId}_${isMainStream ? RemoteStreamType.Main : RemoteStreamType.Aux}`;
          const remoteStreamStat = this._remoteStreamStatMap.get(key);
          if (!remoteStreamStat) return;
          // 若未订阅流，则无需处理
          if (
            (isMainStream && remotePublishedUser?.remoteVideoTrack.isSubscribed) ||
            (isAuxStream && remotePublishedUser?.remoteAuxiliaryTrack)
          ) {
            remoteStreamStat.totalVideoFPS += videoInfoItem.uint32_video_receive_fps!;
            remoteStreamStat.totalVideoBitrate += videoInfoItem.uint32_video_codec_bitrate!;
            remoteStreamStat.videoCount++;
            // 赋值初始宽高
            if (remoteStreamStat.statsToReport.uint32_video_width === 0) {
              remoteStreamStat.statsToReport.uint32_video_width = videoInfoItem.uint32_video_width!;
            }
            if (remoteStreamStat.statsToReport.uint32_video_height === 0) {
              remoteStreamStat.statsToReport.uint32_video_height = videoInfoItem.uint32_video_height!;
            }
            const remoteVideoTrack = isMainStream ? remotePublishedUser.remoteVideoTrack : remotePublishedUser.remoteAuxiliaryTrack;
            if (remoteVideoTrack.stat.jitterBufferDelay) {
              remoteStreamStat.videoJitterBufferDelay = remoteVideoTrack.stat.jitterBufferDelay;
            }
            if (remoteVideoTrack.stat.framesReceived) {
              // 视频渲染比例 = 渲染帧数 / 接收帧数
              remoteStreamStat.statsToReport.uint32_video_consume_render_rate = Math.floor((remoteVideoTrack.stat.framesDecoded / remoteVideoTrack.stat.framesReceived) * 10 ** 6);
            }
          }
        });
        if (!isEmpty(msg_audio_status)) {
          // 只有主流有音频
          const key = `${userId}_${RemoteStreamType.Main}`;
          const remoteStreamStat = this._remoteStreamStatMap.get(key);
          if (this._remoteStreamStatMap.has(key)) {
            if (remoteStreamStat && remotePublishedUser?.remoteAudioTrack.isSubscribed) {
              remoteStreamStat.totalAudioBitrate += msg_audio_status.uint32_audio_codec_bitrate!;
              remoteStreamStat.audioCount++;
              if (remotePublishedUser.remoteAudioTrack.stat.jitterBufferDelay) {
                remoteStreamStat.audioJitterBufferDelay = remotePublishedUser.remoteAudioTrack.stat.jitterBufferDelay;
              }
              // 忽略 audioLevel 为 0 的数据
              if (Math.floor((msg_audio_status.uint32_audio_level! / AUDIO_LEVEL_SCALE) * 100) > 0) {
                remoteStreamStat.totalAudioLevel += msg_audio_status.uint32_audio_level! / AUDIO_LEVEL_SCALE;
                remoteStreamStat.audioLevelCount++;
              }
              if (msg_audio_status.uint32_audio_block_time) {
                remoteStreamStat.statsToReport.uint32_audio_block_time += msg_audio_status.uint32_audio_block_time;
              }
            }
          }
        }
      });

      if (msg_device_info.uint32_audio_capture_cost) {
        this._captureCostSum += msg_device_info.uint32_audio_capture_cost;
        this._captureCostCount += 1;
        if (this._captureCostCount >= 100) {
          this._basicInfo.uint32_audio_capture_cost = Math.floor(this._captureCostSum / this._captureCostCount);
          this._captureCostSum = 0;
          this._captureCostCount = 0;
        }
      }
      if (msg_device_info.uint32_audio_capture_cost === 0 && this._room.audioManager.localAudioTrack?.muted === false) {
        this._localStreamStat.statsToReport.uint32_audio_capture_thread_health_zero_cnt += 1;
      }
    }
  }

  handlePublishStart({ room }: InnerEventTypes[INNER_EVENT.PUBLISH_START][0]) {
    if (this.hitTest(room) && this._localStreamStat.publishStartTime === 0) {
      this._localStreamStat.publishStartTime = Date.now();
    }
  }

  handleTrackCaptureStart({ track }: InnerEventTypes[INNER_EVENT.LOCAL_TRACK_CAPTURE_START][0]) {
    if (track.mediaType === MediaType.AUDIO && !this._pathJoinRoom.uint64_init_audio_start_time) {
      this._pathJoinRoom.uint64_init_audio_start_time = Date.now();
    }

    if (track.mediaType === MediaType.BIG_VIDEO && !this._pathJoinRoom.uint64_init_camera_start_time) {
      this._pathJoinRoom.uint64_init_camera_start_time = Date.now();
    }
  }

  handleTrackCaptureSuccess({ track }: InnerEventTypes[INNER_EVENT.LOCAL_TRACK_CAPTURE_SUCCESS][0]) {
    if (track.mediaType === MediaType.AUDIO && !this._pathJoinRoom.uint64_init_audio_end_time) {
      this._pathJoinRoom.int32_init_audio_ret = 0;
      this._pathJoinRoom.uint64_init_audio_end_time = Date.now();
    }

    if (track.mediaType === MediaType.BIG_VIDEO && !this._pathJoinRoom.uint64_init_camera_end_time) {
      this._pathJoinRoom.int32_init_camera_ret = 0;
      this._pathJoinRoom.uint64_init_camera_end_time = Date.now();
    }
  }

  handleTrackCaptureFailed({ track, error }: InnerEventTypes[INNER_EVENT.LOCAL_TRACK_CAPTURE_FAILED][0]) {
    const GET_USERMEDIA_ERROR_CODE = {
      NotFoundError: 1,
      NotAllowedError: 2,
      NotReadableError: 3,
      OverConstrainedError: 4,
      AbortError: 5,
      InvalidStateError: 6,
      SecurityError: 7,
      TypeError: 8
    };
    const errorCode = GET_USERMEDIA_ERROR_CODE[error.name as keyof typeof GET_USERMEDIA_ERROR_CODE] ||
      (error instanceof RtcError ? error.getExtraCode() || error.getCode() : ErrorCode.UNKNOWN);
    if (track.mediaType === MediaType.AUDIO && !this._pathJoinRoom.uint64_init_audio_end_time) {
      this._pathJoinRoom.int32_init_audio_ret = errorCode;
      this._pathJoinRoom.uint64_init_audio_end_time = Date.now();
    }
    if (track.mediaType === MediaType.BIG_VIDEO && !this._pathJoinRoom.uint64_init_camera_end_time) {
      this._pathJoinRoom.int32_init_camera_ret = errorCode;
      this._pathJoinRoom.uint64_init_camera_end_time = Date.now();
    }
  }

  /**
   * 判断某个 remote userId 是否在进房前就推视频
   * @param {String} userId
   * @return {Boolean}
   * @memberof KeyPointManager
   */
  hasVideoFlag(userId: string) {
    return (
      this._firstPublishedUserList.findIndex(item => item.userId === userId && item.flag & VIDEO_STAT_BIT) >= 0
    );
  }

  /**
   * 判断某个 remote userId 是否在进房前就推音频
   * @param {String} userId
   * @return {Boolean}
   * @memberof KeyPointManager
   */
  hasAudioFlag(userId: string) {
    return (
      this._firstPublishedUserList.findIndex(item => item.userId === userId && item.flag & AUDIO_STAT_BIT) >= 0
    );
  }

  /**
   * 判断某个 remote userId 是否在进房前就推辅流
   * @param {String} userId
   * @return {Boolean}
   * @memberof KeyPointManager
   */
  hasAuxFlag(userId: string) {
    return (
      this._firstPublishedUserList.findIndex(item => item.userId === userId && item.flag & AUX_STAT_BIT) >= 0
    );
  }

  hitTest(room?: IRoom) {
    return room === this._room;
  }

  /**
   * 准备数据
   * @memberof KeyPointManager
   */
  prepareReport() {
    if (this._captureCostCount > 0 && !this._basicInfo.uint32_audio_capture_cost) {
      this._basicInfo.uint32_audio_capture_cost = Math.floor(this._captureCostSum / this._captureCostCount);
      this._captureCostSum = 0;
      this._captureCostCount = 0;
    }
    // 计算上行平均 rtt & loss
    if (this._networkQuality.count > 0) {
      this._basicInfo.uint32_avg_rtt = Math.floor(this._networkQuality.totalUplinkRTT / this._networkQuality.count);
      this._basicInfo.uint32_avg_up_loss = Math.floor(this._networkQuality.totalUplinkLoss / this._networkQuality.count);
    }
    // 如果没有上行 RTT（纯观众场景），使用下行 RTT 的平均值
    if (this._basicInfo.uint32_avg_rtt === 0 && this._networkQuality.totalDownlinkRTTAndLossMap.size > 0) {
      let totalDownlinkRTT = 0;
      let totalDownlinkCount = 0;
      this._networkQuality.totalDownlinkRTTAndLossMap.forEach(data => {
        totalDownlinkRTT += data.totalRTT;
        totalDownlinkCount += data.count;
      });
      if (totalDownlinkCount > 0) {
        this._basicInfo.uint32_avg_rtt = Math.floor(totalDownlinkRTT / totalDownlinkCount);
      }
    }
    // 计算上行平均 fps，码率，分辨率
    if (this._localStreamStat.videoCount > 0) {
      this._localStreamStat.statsToReport.uint32_video_big_capture_fps = Math.floor(this._localStreamStat.totalVideoFPS / this._localStreamStat.videoCount);
      this._localStreamStat.statsToReport.uint32_video_big_bitrate = Math.floor(this._localStreamStat.totalVideoBitrate / this._localStreamStat.videoCount);
      const width = Math.floor(this._localStreamStat.totalVideoWidth / this._localStreamStat.videoCount);
      const height = Math.floor(this._localStreamStat.totalVideoHeight / this._localStreamStat.videoCount);
      this._localStreamStat.statsToReport.uint32_video_big_resolution = (width << 16) | height;
    }
    // 计算上行平均 audioLevel
    if (this._localStreamStat.audioLevelCount > 0) {
      this._localStreamStat.statsToReport.uint32_audio_capture_db = Math.floor((this._localStreamStat.totalAudioLevel / this._localStreamStat.audioLevelCount) * 100);
    }

    // 计算下行平均 fps，码率，audioLevel
    this._remoteStreamStatMap.forEach((data, key) => {
      const { userId } = data;
      // 计算平均下行 loss
      const totalDownlinkRTTAndLossItem = this._networkQuality.totalDownlinkRTTAndLossMap.get(userId);
      if (totalDownlinkRTTAndLossItem) {
        const { totalLoss, count, audioDelayCount, videoDelayCount, totalAudioDelay, totalVideoDelay } = totalDownlinkRTTAndLossItem;
        data.statsToReport.uint32_avg_down_loss = Math.floor(totalLoss / count);
        if (audioDelayCount && totalAudioDelay) {
          data.statsToReport.uint32_audio_network_p2p_delay = Math.floor(totalAudioDelay / audioDelayCount);
          // 音频全链路延迟 = network delay + audio jitter delay
          if (data.audioJitterBufferDelay) {
            data.statsToReport.uint32_p2p_delay = Math.floor(data.statsToReport.uint32_audio_network_p2p_delay + data.audioJitterBufferDelay);
          }
        }
        if (videoDelayCount && totalVideoDelay) {
          data.statsToReport.uint32_video_network_p2p_delay = Math.floor(totalVideoDelay / videoDelayCount);
        }
      }

      // 计算平均视频码率
      if (data.videoCount > 0) {
        data.statsToReport.uint32_video_avg_fps = Math.floor(data.totalVideoFPS / data.videoCount);
        data.statsToReport.uint32_video_avg_bitrate = Math.floor(data.totalVideoBitrate / data.videoCount);
      }
      // 计算平均音频码率
      if (data.audioCount > 0) {
        // eslint-disable-next-line no-multi-assign
        data.statsToReport.uint32_audio_recv_bitrate = data.statsToReport.uint32_audio_bitrate = Math.floor(data.totalAudioBitrate / data.audioCount);
      }
      // 计算平均下行音频 db
      if (data.audioLevelCount > 0) {
        data.statsToReport.uint32_audio_play_db = Math.floor((data.totalAudioLevel / data.audioLevelCount) * 100);
      }

      const { callDurationCalculator } = this._room;
      if (callDurationCalculator) {
        data.statsToReport.uint32_audio_play_time = callDurationCalculator.getDuration(
          key,
          NAME.AUDIO
        );
        data.statsToReport.uint32_video_play_time = callDurationCalculator.getDuration(
          key,
          NAME.VIDEO
        );
      }

      // 过滤视频订阅秒开数据，最大秒开时间 5s
      if (data.statsToReport.uint32_video_render_first) {
        data.statsToReport.uint32_video_render_first = Math.min(
          data.statsToReport.uint32_video_render_first,
          MAX_DURATION_TIME
        );
      }
      // 获取卡顿数据
      const { badCaseDetector } = this._room;
      const { dataFreeze, count: dataFreezeCount } = badCaseDetector.getDataFreezeDuration(key);
      const { renderFreeze, exactRenderFreeze } = badCaseDetector.getRenderFreezeDuration(key);
      // 视频卡顿次数
      data.statsToReport.uint32_video_block_count = dataFreezeCount;
      // 若卡顿时长超过通话时长，则按通话时长统计
      data.statsToReport.uint32_video_block_time = Math.min(
        dataFreeze,
        data.statsToReport.uint32_video_play_time
      );
      data.statsToReport.uint32_video_external_block_time = Math.min(
        renderFreeze,
        data.statsToReport.uint32_video_play_time
      );
      // 精确渲染卡顿时长, 排除主线程卡顿导致 RVFC 跳帧的问题
      data.statsToReport.uint32_video_rendering_block_time = Math.min(
        exactRenderFreeze,
        data.statsToReport.uint32_video_play_time
      );
      // 音频卡顿总时长
      data.statsToReport.uint32_audio_block_time = Math.min(
        data.statsToReport.uint32_audio_block_time,
        data.statsToReport.uint32_audio_play_time
      );
      // 打点记录，表明该流是黑屏流，在后台计算出黑屏率。
      data.statsToReport.uint32_video_black_screen_subjective = 0;
      data.statsToReport.uint32_video_black_screen_strict = 0;
      if (badCaseDetector.isBlackStream(key)) {
        data.statsToReport.uint32_video_black_screen_strict = 1; // 统计包含解码失败导致的黑屏 case
        if (data.statsToReport.uint32_video_avg_fps === 0) {
          data.statsToReport.uint32_video_black_screen_subjective = 1;
        }
      }
      // 上报音频/视频的平均 jitterBuffer 时间
      if (data.audioJitterBufferDelay) {
        data.statsToReport.uint32_audio_cache_time = Math.floor(data.audioJitterBufferDelay);
      }
      if (data.videoJitterBufferDelay) {
        data.statsToReport.uint32_video_cache_time = Math.floor(data.videoJitterBufferDelay);
      }
    });

    // 过滤音频进房秒开数据
    // 1.进房前，若该远端用户没推音频，无需统计音频秒开
    // 2.最大秒开时间 5s，超过则按 5s 统计
    // ref: http://tapd.oa.com/LiteAV_TRTC/prong/stories/view/1020403092865582999
    this._pathMainAudioMap.forEach((item, key) => {
      // 进房前，该远端用户没推音频，无需统计音频秒开
      if (!this.hasAudioFlag(item.userId)) {
        this._pathMainAudioMap.delete(key);
        return;
      }

      // 最大秒开时间 5s
      if (
        item.statsToReport.uint64_play_first_frame_time -
        item.statsToReport.uint64_start_enter_time >
        MAX_DURATION_TIME
      ) {
        item.statsToReport.uint64_play_first_frame_time =
          item.statsToReport.uint64_start_enter_time + MAX_DURATION_TIME;
      }
    });

    // 过滤视频进房秒开数据
    // 1.进房前，若该远端用户没推视频，无需统计视频秒开
    // 2.最大秒开时间 5s，超过则按 5s 统计
    // ref: http://tapd.oa.com/LiteAV_TRTC/prong/stories/view/1020403092865582999
    this._pathMainVideoMap.forEach((item, key) => {
      // 进房前，该远端用户没推视频，无需统计视频秒开
      if (!this.hasVideoFlag(item.userId)) {
        this._pathMainVideoMap.delete(key);
        return;
      }
      if (isUndefined(item.statsToReport.uint64_start_enter_time)) return;
      // 最大秒开时间 5s
      if (
        item.statsToReport.uint64_render_first_frame_time -
        item.statsToReport.uint64_start_enter_time >
        MAX_DURATION_TIME
      ) {
        item.statsToReport.uint64_render_first_frame_time =
          item.statsToReport.uint64_start_enter_time + MAX_DURATION_TIME;
      }
    });

    // 最大进房时间 5s，超过则按 5s 统计
    if (
      this._pathJoinRoom.uint64_end_time - this._pathJoinRoom.uint64_start_time >
      MAX_DURATION_TIME
    ) {
      this._pathJoinRoom.uint64_end_time = this._pathJoinRoom.uint64_start_time + MAX_DURATION_TIME;
    }
  }

  getReportData() {
    this._basicInfo.uint32_networkType = getNumNetworkType();
    // 准备上报数据
    const data = {
      uint32_sdk_app_id: Number(this._room.sdkAppId),
      msg_user_info: new UserInfo({
        userId: this._room.userId,
        tinyId: this._room.tinyId,
        role: this._room.role === UserRole.ANCHOR ? UserRoleNumber.ANCHOR : UserRoleNumber.AUDIENCE
      }),
      msg_basic_info: this._basicInfo,
      uint32_acc_ip: ipv4ToUint32(this._signalInfo.relayIp), // 后台处理 relayIp 使用大端序
      uint32_client_ip: ipv4ToUint32(this._signalInfo.clientIp, false), // 后台处理 client ip 使用小端序
      uint32_acc_port: this._signalInfo.relayPort || 0,
      uint64_timestamp: Date.now(),
      uint32_seq: Math.floor(Math.random() * 2 ** 31),
      msg_path_enter_room: this._pathJoinRoom,
      msg_path_exit_room: this._pathLeaveRoom,
      msg_path_recv_video: [...this._pathMainVideoMap.values(), ...this._subscribedVideoFirstFrameMap.values()].map(item => item.statsToReport),
      msg_quality_statistics: [...this._remoteStreamStatMap.values()].map(item => item.statsToReport),
      str_room_name: String(this._room.roomId || 0),
      msg_path_recv_audio: [...this._pathMainAudioMap.values()].map(item => item.statsToReport),
      uint32_info_client_ip: ipv4ToUint32(this._signalInfo.clientIp, false),
      error_code: [],
      msg_local_statistics: this._localStreamStat.statsToReport,
      bytes_report_buf_from_0x1: this._signalInfo.endReportExtend,
      str_user_sig: this._room.userSig,
      bytes_report_token: this._signalInfo.reportToken
    };
    convertObjectNumberToInt(data);
    return data;
  }

  /**
   * 上报数据
   * 调用时机：进房失败、退房后、开始切换房间
   * @memberof KeyPointManager
  */
  async report() {
    try {
      this.prepareReport();
      const data = this.getReportData();
      this._log.debug(data);
      await this.upload(data);
      this._lastReportData = data;
      // 上报完后重置数据
      this.initData();
    } catch (error) {
      this._log.warn(error);
    } finally {
      if (this.isDestroyed) {
        // @ts-ignore
        this._room = null;
      }
    }
  }

  @addPromiseRetry({ settings: { timeout: 500, retries: 3 } })
  async upload(sourceData: any) {
    // 进房开始时间 = 0，异常数据，不上报
    if (sourceData.msg_path_enter_room.uint64_start_time === 0) {
      return;
    }
    const sdkAppId = Number(this._room.sdkAppId);
    const toUpload = ssoManager.enable ? buildSSOPackage(sourceData, REPORT_TYPE.END_REPORT, sdkAppId) : await compressJSON(sourceData);
    const isCompressed = toUpload instanceof ArrayBuffer;
    const url = `${getLoggerUrl(sdkAppId, LOGGER_CMD_TYPE.KEY_POINT)}&gzip=${+isCompressed}`;
    let sendBeaconResult = false;
    if (navigator.sendBeacon) {
      sendBeaconResult = navigator.sendBeacon(url, toUpload);
    }
    const uploadPromises: (Promise<void>|Promise<any>)[] = [this.uploadKVStat(kvStatManager), this.uploadKVStat(outterKVStatManager)];
    if (!sendBeaconResult) {
      uploadPromises.push(http({ url, body: toUpload, priority: 'low' }));
    }
    await Promise.all(uploadPromises);
  }

  setConnectionType(connectionType: KeyPointConnectionType) {
    this.connectionType = connectionType;
    this._basicInfo.uint32_connection_type = connectionType;
  }

  async uploadKVStat(kvStatManager: KVStatManager, sdkAppId = this._room.sdkAppId) {
    const kvStatData = kvStatManager.getReportData(this._room?.userSig, this._signalInfo?.reportToken);
    if (kvStatData.stats_count.length === 0 && kvStatData.stats_distribution.length === 0) return;
    kvStatData.msg_sdk_basic_info = {
      ...kvStatData.msg_sdk_basic_info,
      bytes_device_name: this._basicInfo.string_device_name || '',
      bytes_os_version: this._basicInfo.string_os_version || '',
      uint32_framework: this._frameWorkType,
      uint32_network_type: this._basicInfo.uint32_networkType || 0
    };
    this._log.debug(kvStatData);
    const toUpload = ssoManager.enable ? buildSSOPackage(kvStatData, REPORT_TYPE.KEY_METRIC_REPORT, sdkAppId) : await compressJSON(kvStatData);
    const isCompressed = toUpload instanceof ArrayBuffer;
    const KV_STAT_URL = `${getLoggerUrl(+sdkAppId, LOGGER_CMD_TYPE.KV_STAT)}&gzip=${+isCompressed}`;
    let kvStatSendBeaconResult = false;
    if (navigator.sendBeacon) {
      kvStatSendBeaconResult = navigator.sendBeacon(KV_STAT_URL, toUpload);
    }
    if (!kvStatSendBeaconResult) {
      http({ url: KV_STAT_URL, body: toUpload });
    }
  }

  getLastReportData() {
    return this._lastReportData;
  }
}

const MAX_DURATION_TIME = 5000;

const REMOTE_STREAM_STATS = {
  msg_user_info: null, // 用户信息带角色和userid，便于麦上麦下区分效果及问题排查
  uint32_video_avg_fps: 0, // 本次通话平均视频帧率
  uint32_video_width: 0, // 本次通话视频宽（取初始值）
  uint32_video_height: 0, // 本次通话视频高（取初始值）
  uint32_video_avg_bitrate: 0, // 本次通话平均视频码率
  uint32_video_block_time: 0, // 本次通话的视频卡顿总时长
  uint32_video_play_time: 0, // 本次通话的视频播放总时长
  uint32_audio_block_time: 0, // 本次通话音频卡顿总时长
  uint32_audio_play_time: 0, // 本次通话的音频播放总时长
  uint32_audio_play_db: 0, // 本次通话的音频播放分贝
  uint32_avg_down_loss: 0, // 本次通话的平均下行loss
  uint32_stream_type: 0, // 流类型 1:音频 2:主流 3:小流 7:辅流
  // uint32_video_render_first: 0, // 订阅秒开时间（发送订阅 cmd 到首帧渲染耗时），max 0xFFFFFFFF = 请求但未渲染，为黑屏
  uint32_video_block_count: 0, // 本次通话的视频卡顿次数
  uint32_audio_block_count: 0, // 本次通话的音频卡顿次数
  uint32_audio_bitrate: 0, // 本次通话的平均音频解码码率，web 拿不到解码码率，按接收码率上报
  uint32_video_black_screen_subjective: 0, // 本次通话的主观黑屏率（包含远端关闭视频等）
  uint32_video_black_screen_strict: 0, // 本次通话的严格黑屏率
  uint32_audio_recv_bitrate: 0, // 本次通话的平均音频接收码率
  uint32_video_external_block_time: 0, // 本次通话的对外视频卡顿总时长
  uint32_video_consume_render_rate: 0,
  uint32_video_cache_time: 0, // 本次通话的平均视频 jitterBuffer 时间
  uint32_audio_cache_time: 0, // 本次通话的平均音频 jitterBuffer 时间
  uint32_video_rendering_block_time: 0 // 本次通话视频渲染卡顿总时长
};

class UserInfo {
  str_identifier: string;
  str_tinyid: string;
  uint32_role: number;
  constructor(options: { userId: any; tinyId: any; role: number; }) {
    this.str_identifier = String(options.userId);
    this.str_tinyid = String(options.tinyId || 0);
    this.uint32_role = options.role;
  }
}
export default KeyPointManager;
